import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Headset } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useSupportChatUI } from "@/contexts/SupportChatContext";
import useLucasWidgetPrefs, { clampPosition } from "./useLucasWidgetPrefs";
import {
  LUCAS_ACCENT_BG,
  LUCAS_ACCENT_TEXT,
  LUCAS_SURFACE,
} from "./lucasTokens";
import useDraggable from "./useDraggable";

/**
 * O atalho do Lucas, presente em toda a plataforma.
 *
 * ## O que fica montado
 *
 * Só este arquivo: um botão e um anel de CSS. A conversa inteira (balões,
 * blocos, markdown) está atrás de um `lazy()` e só desce da rede quando alguém
 * abre. Enquanto ninguém abre, o custo em toda tela da plataforma é um nó fixo
 * e nenhuma requisição: o `useSupportChat` do provider fica dormente até o
 * primeiro gesto.
 *
 * ## Por que não é um segundo chat
 *
 * O painel lê a conversa do provider, que é a mesma que a home desenha. O
 * widget anterior instanciava o próprio hook, e os dois estados divergiam sobre
 * uma única conversa do servidor. Aqui existe um estado só, e esta é a razão de
 * o widget ter voltado a ser possível.
 *
 * ## Por que some na home
 *
 * Na home o Lucas JÁ é a tela: a mesma conversa ocupa a coluna inteira. Deixar
 * a bolinha ali mostraria o mesmo diálogo duas vezes, uma por cima da outra. O
 * atalho existe para levar o Lucas aonde ele não estava.
 */

const LucasWidgetPanel = lazy(() => import("./LucasWidgetPanel"));

const BUBBLE_SIZE = 56;
const PANEL = { width: 380, height: 560, gap: 12 };
const MARGEM = 8;
/** Abaixo disto a conversa não cabe: melhor abrir para o outro lado. */
const ALTURA_MINIMA = 320;

/**
 * Onde o painel cabe, dado onde a bolinha está.
 *
 * Abre ACIMA da bolinha, que é o natural para um atalho no rodapé. Mas a
 * bolinha é arrastável: encostada no topo, não sobra altura em cima, e um
 * painel simplesmente grudado na borda acabava passando POR CIMA dela, com a
 * bolinha flutuando no meio da conversa. Quando falta espaço em cima e sobra
 * embaixo, o painel abre para baixo.
 *
 * Em todos os casos a altura é cortada pelo espaço disponível, nunca
 * invadindo a faixa onde a bolinha está.
 */
export function panelPosition(position, viewport) {
  const right = Math.min(
    Math.max(position.right, MARGEM),
    Math.max(MARGEM, viewport.width - PANEL.width - MARGEM),
  );

  const acima =
    viewport.height - position.bottom - BUBBLE_SIZE - PANEL.gap - MARGEM;
  const abaixo = position.bottom - PANEL.gap - MARGEM;

  if (acima >= ALTURA_MINIMA || acima >= abaixo) {
    return {
      right,
      bottom: position.bottom + BUBBLE_SIZE + PANEL.gap,
      height: Math.max(0, Math.min(PANEL.height, acima)),
    };
  }

  const height = Math.max(0, Math.min(PANEL.height, abaixo));
  return { right, bottom: position.bottom - PANEL.gap - height, height };
}

/** Quanto tempo na tela antes do chamariz. */
const ATRASO_MS = 60_000;
/** Por quanto tempo o anel fica pulsando, uma vez iniciado. */
const JANELA_MS = 45_000;
/** Descanso entre uma pulsada e a próxima. */
export const DESCANSO_MS = 24 * 60 * 60 * 1000;

/**
 * A pulsada de descoberta: no máximo uma por dia.
 *
 * O chamariz contínuo que existia antes (um anel a cada 9s, para sempre) é o
 * que o mercado aprendeu a não fazer: em duas semanas a pessoa aprende a
 * ignorar. A prática consolidada é atrasar a primeira aparição e limitar a
 * frequência.
 *
 * Aqui: espera um minuto na tela e pulsa por 45 segundos. Depois descansa 24
 * horas. Quem já abriu o Lucas alguma vez, ou escondeu o atalho, nunca entra
 * neste caminho.
 *
 * ## Por que por TEMPO, e não por sessão
 *
 * Duas versões antes disto contavam sessão de aba (`sessionStorage`), e as duas
 * pontas estavam erradas: quem trabalha com três abas via o chamariz três
 * vezes, e quem deixa a plataforma aberta a semana inteira o via UMA vez na
 * vida, sendo exatamente o perfil que a gente quer alcançar. Um carimbo de
 * tempo no `localStorage` é compartilhado entre abas e independe de fechar o
 * navegador ou de logar de novo.
 *
 * O recado de verdade (o time respondeu) não passa por aqui: é o ponto de não
 * lido, que não tem limite porque não é chamariz.
 */
function useDiscoveryNudge({ ativo, ultimaPulsada, aoPulsar }) {
  const [pulsando, setPulsando] = useState(false);

  useEffect(() => {
    if (!ativo) return undefined;
    if (Date.now() - ultimaPulsada < DESCANSO_MS) return undefined;

    const inicio = setTimeout(() => {
      aoPulsar();
      setPulsando(true);
    }, ATRASO_MS);

    return () => clearTimeout(inicio);
  }, [ativo, ultimaPulsada, aoPulsar]);

  useEffect(() => {
    if (!pulsando) return undefined;
    const fim = setTimeout(() => setPulsando(false), JANELA_MS);
    return () => clearTimeout(fim);
  }, [pulsando]);

  return pulsando;
}

export default function LucasBubble({ userId, homePath }) {
  const { isOpen, unread, close, toggle } = useSupportChatUI();
  const { pathname } = useLocation();
  const {
    position,
    hidden,
    discovered,
    lastNudgeAt,
    setPosition,
    setHidden,
    markDiscovered,
    markNudged,
  } = useLucasWidgetPrefs(userId);

  // Na home o Lucas já ocupa a tela: ver o cabeçalho deste arquivo.
  const naHome = pathname === homePath;

  const nudging = useDiscoveryNudge({
    ativo: !discovered && !hidden && !naHome,
    ultimaPulsada: lastNudgeAt,
    aoPulsar: markNudged,
  });

  const commitPosition = useCallback(
    (next) => {
      setPosition(
        clampPosition(next, {
          width: window.innerWidth,
          height: window.innerHeight,
        }),
      );
    },
    [setPosition],
  );

  const { nodeRef, consumeDrag, dragProps } = useDraggable({
    position,
    onCommit: commitPosition,
  });

  /**
   * Abrir pela sidebar traz a bolinha de volta.
   *
   * Sem isto, quem escondeu o atalho e depois clicou em "Suporte" veria o painel
   * surgir preso a uma bolinha invisível, sem jeito de arrastar nem de fechar o
   * ciclo. Esconder é sobre o atalho, não sobre o Lucas.
   */
  useEffect(() => {
    if (isOpen && hidden) setHidden(false);
  }, [isOpen, hidden, setHidden]);

  // Uma posição guardada numa tela maior deixaria a bolinha fora da janela.
  useEffect(() => {
    const onResize = () => {
      const safe = clampPosition(position, {
        width: window.innerWidth,
        height: window.innerHeight,
      });
      if (safe.right !== position.right || safe.bottom !== position.bottom) {
        setPosition(safe);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [position, setPosition]);

  const handleClick = useCallback(() => {
    // Soltar a bolinha num lugar novo não é pedir para abrir.
    if (consumeDrag()) return;
    markDiscovered();
    toggle();
  }, [consumeDrag, markDiscovered, toggle]);

  const panelStyle = useMemo(() => {
    if (typeof window === "undefined") return null;
    const spot = panelPosition(position, {
      width: window.innerWidth,
      height: window.innerHeight,
    });
    // A altura já vem calculada contra o espaço livre: cortá-la de novo aqui
    // era o que fazia o painel invadir a faixa da bolinha.
    return {
      right: spot.right,
      bottom: spot.bottom,
      width: PANEL.width,
      height: spot.height,
    };
  }, [position]);

  if (naHome) return null;
  if (hidden && !isOpen) return null;

  return (
    /**
     * `pointer-events-none` no contêiner é o que mantém o canvas do flow e a
     * lista do Inbox utilizáveis por baixo: só a bolinha e o painel recebem
     * ponteiro, o resto da área atravessa.
     */
    <div className="pointer-events-none fixed inset-0 z-[60]">
      {isOpen && panelStyle ? (
        <div className="pointer-events-auto fixed" style={panelStyle}>
          <Suspense fallback={null}>
            <LucasWidgetPanel
              onClose={close}
              onHide={() => {
                close();
                setHidden(true);
              }}
              homePath={homePath}
            />
          </Suspense>
        </div>
      ) : null}

      {/**
       * O anel e o botão são IRMÃOS, não pai e filho.
       *
       * Dentro do botão, o anel cobria a bolinha: no pico da pulsada ela ficava
       * verde inteira, e se a animação não chegasse a ser gerada (config do
       * Tailwind sem recarregar, por exemplo) o anel virava um disco verde
       * opaco permanente. Como irmão desenhado ANTES, o botão pinta por cima e
       * só sobra o halo que escapa das bordas, que é o efeito pretendido.
       *
       * O arrasto mora neste contêiner para que anel e botão se movam juntos.
       */}
      <div
        ref={nodeRef}
        {...dragProps}
        style={{
          right: position.right,
          bottom: position.bottom,
          width: BUBBLE_SIZE,
          height: BUBBLE_SIZE,
        }}
        data-lucas-launcher
        className="pointer-events-auto fixed touch-none select-none"
      >
        {/* Decorativo. `opacity-0` é o estado base: sem a animação aplicada
            ele some, em vez de virar um disco sólido na frente da bolinha. */}
        {nudging && !isOpen ? (
          <span
            aria-hidden="true"
            className={`absolute inset-0 animate-lucas-nudge rounded-[18px] opacity-0 motion-reduce:animate-none ${LUCAS_ACCENT_BG}`}
          />
        ) : null}

        <button
          type="button"
          onClick={handleClick}
          aria-label={
            isOpen
              ? "Fechar o Lucas"
              : unread > 0
                ? `Abrir o Lucas, ${unread} ${unread === 1 ? "resposta nova" : "respostas novas"}`
                : "Abrir o Lucas"
          }
          aria-expanded={isOpen}
          title="Lucas: tire dúvidas sobre a plataforma"
          /**
           * Quadrado arredondado escuro, com o ícone em verde.
           *
           * ## A forma
           *
           * O círculo é a convenção dos lançadores de chat do mercado, mas era
           * o único elemento perfeitamente redondo da plataforma: sidebar,
           * botões, cards e o próprio composer falam em `rounded-[18px]` e
           * vizinhança. O mesmo raio aqui faz o atalho parecer parte da casa em
           * vez de um adesivo colado por cima.
           *
           * ## A cor
           *
           * Passou por dois extremos antes. `BTN_SOLID` (branco no tema escuro)
           * lia como corpo estranho; verde chapado resolvia o tema mas era peso
           * demais em repouso, para um recurso que a maior parte do dia ninguém
           * usa. Fundo escuro com o ícone em verde mantém a identidade do Lucas
           * e devolve a tela ao conteúdo.
           *
           * ## A superfície segue o tema
           *
           * Passou por três versões antes desta, e cada uma morreu por fixar um
           * valor: branco fixo (o token de ação primária) virava um disco
           * estranho na tela escura; verde chapado pesava demais em repouso;
           * escuro fixo resolvia o claro (16.8 de contraste) e sumia no escuro
           * (1.10), e depois pesava na tela clara.
           *
           * A resposta é a mesma do painel da conversa: seguir o tema. Os dois
           * são a mesma coisa e têm que parecer da mesma família. `LUCAS_SURFACE`
           * carrega esse par, e a silhueta vem da borda e da sombra, não do
           * preenchimento.
           *
           * Quem faz barulho quando precisa é a pulsada e o ponto de não lido,
           * não a cor de base.
           */
          className={`pointer-events-auto absolute inset-0 flex items-center justify-center rounded-[18px] shadow-[0_8px_26px_rgba(0,0,0,.18)] outline-none transition-[opacity,box-shadow] hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#17855f] active:cursor-grabbing dark:focus-visible:ring-[#40dea8] ${LUCAS_SURFACE} ${LUCAS_ACCENT_TEXT}`}
        >
          <Headset className="h-5 w-5" />
        </button>

        {/**
         * O time respondeu e ninguém leu.
         *
         * Este é o único aviso sem teto, porque não é chamariz: é recado. Fica
         * até a conversa ser aberta, e some sozinho quando ela for.
         */}
        {unread > 0 && !isOpen ? (
          <span
            aria-hidden="true"
            // O anel tem a cor do PRÓPRIO atalho, não a da página: o recorte
            // acompanha o botão em qualquer tela por baixo, e por isso segue os
            // mesmos dois valores de tema que o preenchimento dele.
            className="pointer-events-none absolute -right-1 -top-1 grid h-5 min-w-[20px] place-items-center rounded-full bg-[#e5443a] px-1 text-[11px] font-bold leading-none text-white ring-2 ring-white dark:ring-[#12131b]"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        ) : null}
      </div>
    </div>
  );
}
